// ✅ src/screens/LeaderboardScreen.js (FULL) — Top Gifters / Top Gifted (CLEAN)
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

// ✅ pick field based on mode
function pickGiftScore(profile, mode) {
  if (mode === "gifters") return num(profile?.giftCoinsSent, 0);
  return num(profile?.giftCoinsReceived, 0);
}

/* ---------------- main ---------------- */
export default function LeaderboardScreen() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ 2 leaderboards
  const [mode, setMode] = useState("gifters"); // "gifters" | "gifted"

  async function loadLeaderboard(nextMode = mode) {
    const field = nextMode === "gifters" ? "giftCoinsSent" : "giftCoinsReceived";
    setLoading(true);

    try {
      // ✅ Best: ordered query
      const qy = query(collection(db, "datingProfiles"), orderBy(field, "desc"), limit(50));
      const snap = await getDocs(qy);

      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setRows(list);
    } catch (e) {
      // ✅ fallback: fetch more then sort locally
      try {
        const qy2 = query(collection(db, "datingProfiles"), limit(200));
        const snap2 = await getDocs(qy2);

        const list2 = snap2.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setRows(list2);
      } catch {
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLeaderboard(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ✅ Sort + remove zeros + stable ties
  const sorted = useMemo(() => {
    const copy = Array.isArray(rows) ? [...rows] : [];

    copy.sort((a, b) => {
      const sa = pickGiftScore(a, mode);
      const sb = pickGiftScore(b, mode);
      if (sb !== sa) return sb - sa;

      // stable tie-breakers
      const na = safeStr(a?.displayName).toLowerCase();
      const nb = safeStr(b?.displayName).toLowerCase();
      if (na !== nb) return na < nb ? -1 : 1;

      const ia = safeStr(a?.id);
      const ib = safeStr(b?.id);
      return ia < ib ? -1 : ia > ib ? 1 : 0;
    });

    // ✅ remove people with 0 (only show real gifters/gifted)
    const nonZero = copy.filter((p) => pickGiftScore(p, mode) > 0);

    return nonZero.slice(0, 50);
  }, [rows, mode]);

  const title = mode === "gifters" ? "🎁 Top Gifters" : "💝 Top Gifted";
  const sub = mode === "gifters" ? "Most coins spent on gifts" : "Most coins received from gifts";
  const scoreLabel = mode === "gifters" ? "Sent" : "Received";

  return (
    <div style={S.wrap}>
      <div style={S.gridHeader}>
        <div style={{ minWidth: 0 }}>
          <div style={S.gridTitle}>{title}</div>
          <div style={S.gridSubHead}>{sub}</div>
        </div>

        <div style={S.headerRight}>
          <div style={S.tabs}>
            <button
              type="button"
              onClick={() => setMode("gifters")}
              style={{ ...S.tabBtn, ...(mode === "gifters" ? S.tabActive : null) }}
            >
              🎁 Gifters
            </button>
            <button
              type="button"
              onClick={() => setMode("gifted")}
              style={{ ...S.tabBtn, ...(mode === "gifted" ? S.tabActive : null) }}
            >
              💝 Gifted
            </button>
          </div>

          <button type="button" style={S.secondaryPillBtn} onClick={() => loadLeaderboard(mode)}>
            ♻️ Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={S.blankState}>
          <div style={S.blankStateText}>Loading…</div>
        </div>
      ) : !sorted.length ? (
        <div style={S.blankState}>
          <div style={S.blankStateText}>
            Nothing here yet.
            <div style={{ marginTop: 6, fontWeight: 900, opacity: 0.65, fontSize: 12 }}>
              Send a gift to make the leaderboard appear.
            </div>
          </div>
        </div>
      ) : (
        <div style={S.list}>
          {sorted.map((p, idx) => {
            const name = safeStr(p?.displayName) || "User";
            const avatar = safeStr(p?.avatar) || safeStr(p?.photos?.[0]);
            const city = safeStr(p?.city) || safeStr(p?.location) || "—";
            const score = pickGiftScore(p, mode);

            return (
              <div key={p.id} style={S.row}>
                <div style={S.rank}>
                  {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                </div>

                <div style={S.avatarWrap}>
                  {avatar ? (
                    <img src={avatar} alt={name} style={S.avatarImg} />
                  ) : (
                    <div style={S.avatarFallback}>{initials(name)}</div>
                  )}
                </div>

                <div style={S.meta}>
                  <div style={S.name}>{name}</div>
                  <div style={S.small}>📍 {city}</div>
                </div>

                <div style={S.scoreBox}>
                  <div style={S.score}>{score}</div>
                  <div style={S.small}>{scoreLabel}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- styles (NO movement) ---------------- */
const S = {
  wrap: { display: "grid", gap: 12 },

  headerRight: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  tabs: {
    display: "flex",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.62)",
    boxShadow: "0 12px 28px rgba(2,6,23,0.06)",
  },
  tabBtn: {
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    transition: "none",
  },
  tabActive: {
    background: "rgba(2,6,23,0.92)",
    color: "#fff",
    border: "1px solid rgba(2,6,23,0.35)",
  },

  secondaryPillBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
    transition: "none",
  },

  blankState: {
    borderRadius: 24,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.58)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.06)",
    backdropFilter: "blur(12px)",
    padding: 18,
    minHeight: 180,
    display: "grid",
    placeItems: "center",
    textAlign: "center",
  },
  blankStateText: { fontWeight: 900, opacity: 0.65 },

  gridHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  gridTitle: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.3 },
  gridSubHead: { fontWeight: 900, opacity: 0.65, fontSize: 13 },

  list: { display: "grid", gap: 12 },

  row: {
    display: "grid",
    gridTemplateColumns: "64px 52px 1fr 90px",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 20,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 16px 40px rgba(2,6,23,0.08)",
    transition: "none",
  },

  rank: { fontWeight: 1000, opacity: 0.9, textAlign: "center" },

  avatarWrap: {
    width: 46,
    height: 46,
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.75)",
    boxShadow: "0 10px 24px rgba(2,6,23,0.06)",
    flexShrink: 0,
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  avatarFallback: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    background: "rgba(2,6,23,0.85)",
    color: "#fff",
    fontWeight: 1000,
  },

  meta: { minWidth: 0 },
  name: {
    fontWeight: 1000,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 14,
    lineHeight: "18px",
    height: 18,
  },
  small: {
    fontWeight: 900,
    opacity: 0.65,
    fontSize: 12,
    lineHeight: "16px",
    height: 16,
  },

  scoreBox: { textAlign: "right", display: "grid", justifyItems: "end", gap: 2 },
  score: { fontWeight: 1000, fontSize: 16, letterSpacing: -0.2, lineHeight: "18px", height: 18 },
};